/// <reference types="styled-jsx" />

import raf from "raf";
import React, { createRef } from "react";

import {
  CanvasThrobberArc as Arc,
  typeOfCanvasThrobberArc,
} from "./CanvasThrobberArc";
import {
  CanvasThrobberEllipse as Ellipse,
  typeOfCanvasThrobberEllipse,
} from "./CanvasThrobberEllipse";
import {
  CanvasThrobberLine as Line,
  typeOfCanvasThrobberLine,
} from "./CanvasThrobberLine";
import {
  CanvasThrobberRect as Rect,
  typeOfCanvasThrobberRect,
} from "./CanvasThrobberRect";
import { typeOfCanvasThrobberVariation } from "./CanvasThrobberVariation";

declare module "react" {
  interface StyleHTMLAttributes<T> {
    jsx?: boolean
    global?: boolean
  }
}

/**
 * Specification of the props available on the canvas throbber
 */
export type CanvasThrobberProps = {
  children: React.ReactNode;

  /**
   * The color of the elements that are drawn inside the canvas. Default value
   * is #ddd.
   */
  color?: string;

  /**
   * If set to false, the circle around the throbber will visually not be
   * drawn initially. Default value is true.
   */
  drawCircle?: boolean;

  /**
   * The duration specified in milliseconds of a full animation cycle. Default
   * value is 3300.
   */
  duration?: number;

  /**
   * The size of the throbber in pixels (this will be used to apply both width
   * and height to the throbber). Default value is 64.
   */
  size?: number;
};

/**
 * Specifies configurations of different shapes
 */
type CanvasThrobberShape = {
  shape: typeof Rect | typeof Ellipse | typeof Line | typeof Arc;
  config: CanvasThrobberShapeConfig;
};

/**
 * Specifies how multiple shapes are combined into single easing curves
 */
interface ICanvasThrobberEasingCurves {
  [key: string]: CanvasThrobberShapeConfig[];
}

type CanvasThrobberShapeConfig = {
  delay: number;
  duration: number;
  ease: string | ((progress: number) => number);
  easeDelay?: number;
  easeDuration?: number;
};

type CanvasThrobberRectConfig = CanvasThrobberShapeConfig & {
  x: number;
  y: number;
  w: number;
  h: number;
};

type CanvasThrobberLineConfig = CanvasThrobberShapeConfig & {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  w: number;
};

type CanvasThrobberEllipseConfig = CanvasThrobberShapeConfig & {
  x: number;
  y: number;
  radiusX: number;
  radiusY: number;
};

type CanvasThrobberArcConfig = CanvasThrobberEllipseConfig & {
  w: number;
};

/**
 * Renders a circular throbber using a canvas, which will contain an animation
 * of an arrow going down followed by an arbitrary drawing defined using the
 * available child-components.
 */
export class CanvasThrobber extends React.PureComponent<CanvasThrobberProps> {
  private canvasRef = createRef<HTMLCanvasElement>();

  /**
   * Will contain a timestamp for when the canvas was mounted into the DOM, so
   * we can coordinate runtime of the application.
   */
  private tstamp: number;

  /**
   * Will contains a cached reference to the canvas element.
   */
  private canvas: HTMLCanvasElement;

  /**
   * Will contain a reference to the canvas rendering context.
   */
  private ctx: CanvasRenderingContext2D & {
    webkitBackingStorePixelRatio?: number;
    mozBackingStorePixelRatio?: number;
    msBackingStorePixelRatio?: number;
    oBackingStorePixelRatio?: number;
    backingStorePixelRatio?: number;
  };

  /**
   * Contains the elements that should be drawn onto the canvas.
   */
  private drawings: (CanvasThrobberShape | CanvasThrobberShape[])[];

  /**
   * Specifies the rendered size of each "virtual" pixel (all canvas
   * throbbers) are designed to be rendered as 32x32px drawings - upscaling to
   * for instance 64x64px will result in each "virtual" pixel being the
   * equivalent of 2 real pixels.
   */
  private vPX: number;

  /**
   * Specifies the current opacity of the rendered drawing
   */
  private opacity: number;

  /**
   * Will contain the ID of the next pending animation frame, as returned by
   * the raf library.
   */
  private animationFrame: number;

  /**
   * Specifies the ratio between the device pixel ratio and the canvas'
   * internal backing store pixel ratio.
   */
  private ratio: number;

  /**
   * Render the application through React
   */
  public render(): React.JSX.Element {
    // Extract size
    const size = this.props.size || 64;

    // Configure styling
    const style = {
      height: `${size}px`,
      marginLeft: `-${Math.round(size / 2)}px`,
      marginTop: `-${Math.round(size / 2)}px`,
      width: `${size}px`,
    };

    return (
      <React.Fragment>
        <canvas ref={this.canvasRef} style={style} />

        {/* tslint:disable-next-line react-tsx-curly-spacing no-multiline-string */}
        <style jsx>{`
          canvas {
            position: absolute;
            top: 50%;
            left: 50%;
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Bind the onAnimationFrame-loop once the component is mounted to the DOM,
   * so the throbber will begin animating itself.
   */
  public componentDidMount(): void {
    const duration =
      this.props.duration || 3300;
    const size = this.props.size || 64;

    // Calculate timestamp from which animation-frame should be calculated,
    // and create a reference to the canvas-context
    this.tstamp =
      Date.now() + (this.props.drawCircle ? (duration / 33) * 8 : 0);
    this.canvas = this.canvasRef.current as HTMLCanvasElement;

    // According to TypeScript, getContext might return null... In that
    // case we can't do much else than to throw an error to the console
    const ctx = this.canvas.getContext("2d");

    if (ctx === null) {
      throw new Error(
        "Canvas.componentDidMount(): canvas.getContext('2d') returned null."
      );
    }

    this.ctx = ctx;

    // Calculate pixel-ratio
    const devicePixelRatio = window.devicePixelRatio || 1;
    const backingStoreRatio =
      this.ctx.webkitBackingStorePixelRatio ||
      this.ctx.mozBackingStorePixelRatio ||
      this.ctx.msBackingStorePixelRatio ||
      this.ctx.oBackingStorePixelRatio ||
      this.ctx.backingStorePixelRatio ||
      1;

    this.ratio = devicePixelRatio / backingStoreRatio;

    // Fix size of canvas to match this ratio
    this.canvas.width = size * this.ratio;
    this.canvas.height = size * this.ratio;

    // Parse out children and determine what to render inside the throbber
    this.drawings = this.parseChildren(this.props.children, true);

    // Begin the animation-frame...
    this.onAnimationFrame();
  }

  /**
   * Lifecycle-event we use to determine if children are changed, in that
   * case we make sure to parse out the configurations of what to render.
   */
  public componentDidUpdate(): void {
    this.drawings = this.parseChildren(this.props.children, true);
  }

  /**
   * Stop the onAnimationFrame-loop when the component is unmounted from the
   * DOM to save resources.
   */
  public componentWillUnmount(): void {
    raf.cancel(this.animationFrame);
  }

  /**
   * Internal parser that loops over all children inside the throbber, and
   * extracts configurations of what to render inside the throbber.
   */
  private parseChildren(
    children: React.ReactNode,
    allowVariations: false
  ): CanvasThrobberShape[];
  private parseChildren(
    children: React.ReactNode,
    allowVariations: true
  ): (CanvasThrobberShape | CanvasThrobberShape[])[];
  private parseChildren(
    children: React.ReactNode,
    allowVariations: boolean
  ): (CanvasThrobberShape | CanvasThrobberShape[])[] {
    let shapes: CanvasThrobberShape[] = [];
    let variations: CanvasThrobberShape[][] = [];

    // Fetch list of variations and shapes
    React.Children.toArray(children).forEach((child) => {
      // Skip pure text elements...
      if (
        typeof child === "string" ||
        typeof child === "number" ||
        typeof child === "bigint" ||
        !("type" in child)
      ) {
        return;
      }

      // tslint:disable-next-line no-any
      switch (child.type) {
        case typeOfCanvasThrobberVariation:
          // tslint:disable-next-line no-unsafe-any
          variations.push(this.parseChildren((child.props as any).children, false));
          break;

        default:
          shapes.push({
            config: { ...child.props as any },
            shape: child.type as any, // tslint:disable-line no-any
          });
      }
    });

    // Merge sibling shapess into variation-list (both cannot exist on the
    // same level)
    if (variations.length && shapes.length) {
      variations.push(shapes);
    }

    // Filter away empty result-sets
    variations = variations.filter((variation) => variation.length > 0);
    shapes = shapes.filter((tmp) => !!tmp);

    // Return a set of variations?
    if (allowVariations && variations.length) {
      // Loop through and parse out easing-curves for all variations
      return variations.map((variation) => this.parseEasingCurves(variation));
    }

    // ... Or a set of shapes?
    return this.parseEasingCurves(shapes);
  }

  /**
   * This internal helper loops through all shapes extracted in the
   * [[parseChildren]] method, and determines if any of the matched shapes
   * should share easing-curves.
   */
  private parseEasingCurves(
    shapes: CanvasThrobberShape[]
  ): CanvasThrobberShape[] {
    const curves: ICanvasThrobberEasingCurves = {};

    // Extract all shapes that have shared easing curves
    shapes.forEach((shape) => {
      if (!shape.config.ease || typeof shape.config.ease === "function") {
        return;
      }

      if (!curves[shape.config.ease]) {
        curves[shape.config.ease] = [];
      }
      curves[shape.config.ease].push(shape.config);
    });

    // Loop through all curves and determine their starting offset as well
    // as their total duration and then prepare a final easing-method that
    // should be used for the shape
    Object.keys(curves).forEach((curve) => {
      // Get combined delay + duration
      let delay = 100;
      let duration = 0;

      curves[curve].forEach((shape) => {
        delay = Math.min(shape.delay, delay);
      });

      curves[curve].forEach((shape) => {
        duration = Math.max(shape.duration + (shape.delay - delay), duration);
      });

      // Update configurations of all shapes following this easing curve
      curves[curve].forEach((shape) => {
        // Provide a custom easing-method that will follow the original
        // duration and delay of this shape
        shape.ease = ((shapeDelayInput, shapeDurationInput) => {
          // How does delay and duration relate to the combined
          // easing curve?
          const shapeDelay = (shapeDelayInput - delay) / duration;
          const shapeDuration = shapeDurationInput / duration;

          return (percent: number) => {
            // Wait with starting before delay runs out
            if (percent < shapeDelay) {
              return 0;
            }

            // Finish early?
            if (percent >= shapeDelay + shapeDuration) {
              return 1;
            }

            // Ease within defined part of the curve
            return (percent - shapeDelay) / shapeDuration;
          };
        })(shape.delay, shape.duration);

        // Share timing configurations
        shape.easeDelay = delay;
        shape.easeDuration = duration;
      });
    });

    return shapes;
  }

  /**
   * Internal render-method that will make sure that the throbber is being
   * drawn to the canvas for as long as the throbber is displayed.
   */
  // tslint:disable-next-line max-func-body-length
  private onAnimationFrame = () => {
    const durationProp =
      this.props.duration || 3300;
    const sizeProp = this.props.size || 64;
    const colorProp = this.props.color || "#ddd";

    // Setup misc required properties
    let time = (Date.now() - this.tstamp) % durationProp;
    const size = sizeProp * this.ratio;
    const center = Math.round(size / 2);
    const iteration = Math.ceil((Date.now() - this.tstamp) / durationProp);
    const interval = durationProp / 33;

    this.vPX = size / 32;
    this.opacity = 1;

    // No drawing is required from 1100ms to 1200ms and 2400ms to 2850ms,
    // so don't do anything here
    const noDrawing =
      (time > interval * 11 && time < interval * 12) ||
      (time > interval * 24 && time < interval * 28.5);

    if (!noDrawing) {
      // Clear canvas
      this.ctx.clearRect(0, 0, size, size);

      this.ctx.save();

      // Setup color
      this.ctx.fillStyle = colorProp;
      this.ctx.strokeStyle = colorProp;
      this.ctx.lineWidth = this.vPX;

      // Draw circle for the first 800ms
      if (time < 0) {
        time = interval * 8 + time;

        this.ctx.beginPath();
        // tslint:disable-next-line max-line-length
        this.ctx.arc(
          center,
          center,
          center - this.vPX / 2,
          Math.PI * -0.5,
          Math.PI * -0.5 +
            Math.PI * 2 * this.easeInOutQuart(time / (interval * 8))
        );
        this.ctx.stroke();
      } else {
        // Draw the circle
        this.ctx.beginPath();
        this.ctx.arc(center, center, center - this.vPX / 2, 0, Math.PI * 2);
        this.ctx.stroke();

        // Create a circular clipping path
        this.ctx.beginPath();
        this.ctx.arc(center, center, center - this.vPX / 2, 0, Math.PI * 2);
        this.ctx.clip();

        // Draw arrow from 0ms to 1100ms
        if (time > 0 && time < interval * 11) {
          let offset = this.vPX * -13;
          const centerPath = this.vPX * 22.5;

          // Fall down during first 400ms
          if (time < interval * 4) {
            offset += centerPath * this.easeInOutQuart(time / (interval * 4));

            // Fall down during last 400ms
          } else if (time > interval * 7) {
            offset +=
              centerPath +
              centerPath *
                this.easeInOutQuart((time - interval * 7) / (interval * 4));

            // Keep in center for 300ms
          } else {
            offset += centerPath;
          }

          this.ctx.beginPath();
          this.ctx.moveTo(this.vPX * 13, offset);
          this.ctx.lineTo(this.vPX * 13, this.vPX * 7 + offset);
          this.ctx.lineTo(this.vPX * 10, this.vPX * 7 + offset);
          this.ctx.lineTo(this.vPX * 16, this.vPX * 13 + offset);
          this.ctx.lineTo(this.vPX * 22, this.vPX * 7 + offset);
          this.ctx.lineTo(this.vPX * 19, this.vPX * 7 + offset);
          this.ctx.lineTo(this.vPX * 19, offset);
          this.ctx.lineTo(this.vPX * 13, offset);
          this.ctx.closePath();
          this.ctx.fill();
        }

        // Draw animation from 1200ms to 2850ms
        if (time > interval * 12 && time < interval * 28.5) {
          const current = time - interval * 12;
          const duration = interval * 12;

          let shapes: CanvasThrobberShape[];

          // If we're drawing different variations, then determine which
          // variation to draw
          if (Array.isArray(this.drawings[0])) {
            shapes = (this.drawings as CanvasThrobberShape[][])[
              (iteration - 1) % this.drawings.length
            ];
          } else {
            shapes = this.drawings as CanvasThrobberShape[];
          }

          // Loop through all shapes and begin drawing them...
          shapes.forEach((shape) => {
            const shapeDelay =
              shape.config.easeDelay === undefined
                ? shape.config.delay
                : shape.config.easeDelay;
            const shapeDuration =
              shape.config.easeDuration === undefined
                ? shape.config.duration
                : shape.config.easeDuration;

            // Calculate percentage of rendering based on delay and
            // duration
            let percent = this.easeInOutQuart(
              Math.max(0, current - (duration / 100) * shapeDelay) /
                ((duration / 100) * shapeDuration)
            );

            // Apply custom easing?
            if (typeof shape.config.ease === "function") {
              percent = shape.config.ease(percent);
            }

            // Draw the shape...
            switch (shape.shape) {
              case typeOfCanvasThrobberRect:
                const rectConfig = shape.config as CanvasThrobberRectConfig;

                this.drawRect({
                  // tslint:disable object-literal-sort-keys
                  x: rectConfig.x * this.vPX,
                  y: rectConfig.y * this.vPX,
                  w: rectConfig.w * this.vPX,
                  h: rectConfig.h * this.vPX,
                  percent,
                  // tslint:enable object-literal-sort-keys
                });
                break;

              case typeOfCanvasThrobberLine:
                const lineConfig = shape.config as CanvasThrobberLineConfig;

                this.drawLine({
                  // tslint:disable object-literal-sort-keys
                  x1: lineConfig.x1 * this.vPX,
                  y1: lineConfig.y1 * this.vPX,
                  x2: lineConfig.x2 * this.vPX,
                  y2: lineConfig.y2 * this.vPX,
                  w: lineConfig.w,
                  percent,
                  // tslint:enable object-literal-sort-keys
                });
                break;

              case typeOfCanvasThrobberEllipse:
                const ellipseConfig =
                  shape.config as CanvasThrobberEllipseConfig;

                this.drawEllipse({
                  // tslint:disable object-literal-sort-keys
                  x: ellipseConfig.x * this.vPX,
                  y: ellipseConfig.y * this.vPX,
                  radiusX: ellipseConfig.radiusX * this.vPX,
                  radiusY: ellipseConfig.radiusY * this.vPX,
                  percent,
                  // tslint:enable object-literal-sort-keys
                });
                break;

              case typeOfCanvasThrobberArc:
                const arcConfig = shape.config as CanvasThrobberArcConfig;

                this.drawArc({
                  // tslint:disable object-literal-sort-keys
                  x: arcConfig.x * this.vPX,
                  y: arcConfig.y * this.vPX,
                  radiusX: arcConfig.radiusX * this.vPX,
                  radiusY: arcConfig.radiusY * this.vPX,
                  w: arcConfig.w,
                  percent,
                  // tslint:enable object-literal-sort-keys
                });
                break;

              default:
              // No other types available currently...
            }
          });
        }
      }

      this.ctx.restore();
    }

    // Rotate throbber last 700ms
    if (time > interval * 25) {
      const rotate =
        this.easeInOutQuart((time - interval * 25) / (interval * 7)) * 180;
      const axis = iteration % 2 ? "rotateY" : "rotateX";

      this.canvas.style.transform = `${axis}(${rotate}deg)`;
      this.canvas.style.webkitTransform = `${axis}(${rotate}deg)`;
    } else {
      this.canvas.style.transform = "";
      this.canvas.style.webkitTransform = "";
    }

    // Run again on next available animation-frame
    this.animationFrame = raf(this.onAnimationFrame);
  };

  /**
   * Drawing-method that will draw a rect with the specified coordinates,
   * automatically animating it into view.
   */
  private drawRect({
    x,
    y,
    w,
    h,
    percent,
  }: {
    x: number;
    y: number;
    w: number;
    h: number;
    percent: number;
  }): void {
    this.ctx.globalAlpha = this.opacity * percent;
    this.ctx.fillRect(
      x + (w * (1 - percent)) / 2,
      y + (h * (1 - percent)) / 2,
      w * percent,
      h * percent
    );
    this.ctx.globalAlpha = this.opacity;
  }

  /**
   * Drawing-method that will draw an ellipse with the specified coordinates,
   * automatically animating it into view.
   */
  private drawEllipse({
    x,
    y,
    radiusX,
    radiusY,
    percent,
  }: {
    x: number;
    y: number;
    radiusX: number;
    radiusY: number;
    percent: number;
  }): void {
    this.ctx.globalAlpha = this.opacity * percent;
    this.beginEllipse({ x, y, radiusX, radiusY, percent });
    this.ctx.fill();
    this.ctx.globalAlpha = this.opacity;
  }

  /**
   * Drawing-method that will draw an arc with the specified coordinates,
   * automatically animating it into view.
   */
  private drawArc({
    x,
    y,
    radiusX,
    radiusY,
    w,
    percent,
  }: {
    x: number;
    y: number;
    radiusX: number;
    radiusY: number;
    w: number;
    percent: number;
  }): void {
    this.ctx.globalAlpha = this.opacity * percent;
    this.ctx.lineWidth = this.vPX * w;
    this.beginEllipse({
      x,
      y,
      radiusX: radiusX - (this.vPX * w) / 2,
      radiusY: radiusY - (this.vPX * w) / 2,
      percent,
    });
    this.ctx.stroke();
    this.ctx.globalAlpha = this.opacity;
    this.ctx.lineWidth = this.vPX;
  }

  /**
   * Helper-method that begins an elliptical path, which can then be filled
   * to draw an ellipse or stroked to draw an arc.
   */
  private beginEllipse({
    x,
    y,
    radiusX,
    radiusY,
    percent,
  }: {
    x: number;
    y: number;
    radiusX: number;
    radiusY: number;
    percent: number;
  }): void {
    // Animate drawing
    const radiusXProgress = radiusX * percent;
    const radiusYProgress = radiusY * percent;

    // Draw the path
    this.ctx.beginPath();
    this.ctx.moveTo(x, y - radiusYProgress);

    this.ctx.bezierCurveTo(
      x + radiusXProgress,
      y - radiusYProgress,
      x + radiusXProgress,
      y + radiusYProgress,
      x,
      y + radiusYProgress
    );

    this.ctx.bezierCurveTo(
      x - radiusXProgress,
      y + radiusYProgress,
      x - radiusXProgress,
      y - radiusYProgress,
      x,
      y - radiusYProgress
    );
  }

  /**
   * Drawing-method that will draw a line with the specified coordinates,
   * automatically animating it into view.
   */
  private drawLine({
    x1,
    y1,
    x2,
    y2,
    w,
    percent,
  }: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    w: number;
    percent: number;
  }): void {
    this.ctx.lineWidth = this.vPX * w;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1 + (this.vPX * w) / 2);
    this.ctx.lineTo(
      x1 + (x2 - x1) * percent,
      y1 + (this.vPX * w) / 2 + (y2 - y1) * percent
    );
    this.ctx.stroke();
    this.ctx.lineWidth = this.vPX;
  }

  /**
   * Internal helper-method that performs an easeInOutQuart-algorithm on a
   * number from 0 to 1.
   */
  private easeInOutQuart(percent: number): number {
    /**
     * Internal helper that performs easing in.
     */
    function easeIn(percent2: number): number {
      return Math.pow(percent2, 4);
    }

    const cappedPercent = Math.max(0, Math.min(1, percent));

    return cappedPercent < 0.5
      ? easeIn(cappedPercent * 2) / 2
      : 1 - easeIn(cappedPercent * -2 + 2) / 2;
  }
}
